import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "@/api/client";
import { noteStatusPoll } from "@/api/connectivity";
import { noteServerBuild } from "@/api/loadedBuild";
import { keys } from "@/api/queryKeys";
import type { PublicMaintenance } from "@/api/types";

export type AppVersion = {
  version: string;
  commit_sha: string;
  built_at: string;
  // Most recent distinct earlier build the backend has booted
  // from — rollback target for operators / CI smoke on a
  // bad release. Empty string when unknown (first boot / no
  // build-history file).
  previous_version?: string;
  previous_commit_sha?: string;
  previous_built_at?: string;
  /** Absent on a backend that predates it — fall back to /auth/me. */
  maintenance?: PublicMaintenance;
};

/** ``GET /api/version``. Readers (the navbar badge, the maintenance
 *  banner) never fetch on their own; ``useServerStatusPoller`` — mounted
 *  once, by ``RequireAuth`` — keeps it fresh. No retry: the poll is the
 *  retry, and a backend that has gone away must show at once. The first
 *  answer is also kept as the build this tab runs (``loadedBuild``). */
export const appVersionQuery = queryOptions({
  queryKey: keys.version,
  queryFn: async () => {
    let answer: AppVersion;
    try {
      answer = (await api.get<AppVersion>("/version")).data;
    } catch (error) {
      noteStatusPoll(error);
      throw error;
    }
    noteStatusPoll(null);
    noteServerBuild(answer);
    return answer;
  },
  staleTime: Infinity,
  gcTime: Infinity,
  retry: false,
});

/** Build metadata + public maintenance state, as last polled. */
export function useAppVersion() {
  return useQuery(appVersionQuery);
}

export type MaintenanceState = {
  enabled: boolean;
  message: string;
  enabled_by: string | null;
  enabled_at: string | null;
  /** Strict mode also blocks platform admins (a deploy window). */
  strict?: boolean;
};

/** Read the current maintenance-mode state. Admin-only endpoint,
 *  used by the MaintenanceTab. Every user sees the public part through
 *  the polled ``/api/version`` (and ``useMe().data.maintenance``), but
 *  this hook is the writable-admin view that also invalidates on
 *  mutation. */
export function useMaintenanceState() {
  return useQuery({
    queryKey: keys.maintenance,
    queryFn: async () =>
      (await api.get<MaintenanceState>("/admin/maintenance")).data,
    staleTime: 15_000,
  });
}

export function useSetMaintenance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { enabled: boolean; message: string; strict?: boolean }) =>
      (await api.post<MaintenanceState>("/admin/maintenance", payload)).data,
    onSuccess: (data) => {
      qc.setQueryData(keys.maintenance, data);
      // The app-wide banner reads the polled /api/version (falling back
      // to /me): refresh both so the flip shows without waiting a poll.
      qc.invalidateQueries({ queryKey: keys.me });
      qc.invalidateQueries({ queryKey: keys.version });
    },
  });
}
